/**
 * The filter row shared by the question bank and the exam builder's picker.
 *
 * Every field maps 1:1 onto `QuestionListQuery`, so filtering is server-side —
 * including `week_no`, which the API resolves through the topic. Nothing here
 * filters a page after the fact.
 *
 * The enum dropdowns render `<Enum>.options` from `@exam/shared` rather than a
 * list of literals. They used to be literals, and when `QuestionSource` gained
 * `transcribed` the 124 questions carrying it became impossible to filter for —
 * present in the bank, absent from the only control that could isolate them.
 * Reading the enum means the next value that is added appears on its own.
 */
import {
  Difficulty,
  QuestionSource,
  QuestionStatus,
  QuestionType,
} from "@exam/shared";
import { Input, Select } from "@/components/ui";
import {
  DIFFICULTY_LABEL,
  SOURCE_LABEL,
  STATUS_LABEL,
  TYPE_LABEL,
  labelFor,
} from "../lib/enumLabels";
import type { Catalog } from "../data/types";

export interface QuestionFilterValues {
  subject_id: string;
  week_no: string;
  topic_id: string;
  type: string;
  difficulty: string;
  status: string;
  source: string;
  q: string;
}

export const EMPTY_FILTERS: QuestionFilterValues = {
  subject_id: "",
  week_no: "",
  topic_id: "",
  type: "",
  difficulty: "",
  status: "",
  source: "",
  q: "",
};

/** Drops the blanks and coerces `week_no`, ready for the query string. */
export function toQuery(values: QuestionFilterValues) {
  return {
    ...(values.subject_id ? { subject_id: values.subject_id } : {}),
    ...(values.week_no ? { week_no: Number(values.week_no) } : {}),
    ...(values.topic_id ? { topic_id: values.topic_id } : {}),
    ...(values.type ? { type: values.type as QuestionType } : {}),
    ...(values.difficulty ? { difficulty: values.difficulty as Difficulty } : {}),
    ...(values.status ? { status: values.status as QuestionStatus } : {}),
    ...(values.source ? { source: values.source as QuestionSource } : {}),
    ...(values.q.trim() ? { q: values.q.trim() } : {}),
  };
}

export function QuestionFilters({
  catalog,
  values,
  onChange,
  /** The bank shows status/source; the exam picker only ever sees approved. */
  showStatus = true,
}: {
  catalog: Catalog;
  values: QuestionFilterValues;
  onChange: (next: QuestionFilterValues) => void;
  showStatus?: boolean;
}) {
  const set = (patch: Partial<QuestionFilterValues>) =>
    onChange({ ...values, ...patch });

  const weeks = values.subject_id
    ? (catalog.weeksBySubject.get(values.subject_id) ?? [])
    : [...new Set(catalog.topics.map((t) => t.week_no).filter((w): w is number => w !== null))].sort(
        (a, b) => a - b,
      );

  const topics = catalog.topics.filter(
    (t) =>
      (!values.subject_id || t.subject_id === values.subject_id) &&
      (!values.week_no || t.week_no === Number(values.week_no)),
  );

  return (
    <div className="flex flex-wrap items-end gap-2">
      <Select
        label="Subject"
        value={values.subject_id}
        // Changing subject invalidates the week and topic below it.
        onChange={(e) => set({ subject_id: e.target.value, week_no: "", topic_id: "" })}
        className="w-52"
      >
        <option value="">All subjects</option>
        {catalog.subjects.map((s) => (
          <option key={s.id} value={s.id}>
            {s.name}
          </option>
        ))}
      </Select>

      <Select
        label="Week"
        value={values.week_no}
        onChange={(e) => set({ week_no: e.target.value, topic_id: "" })}
        className="w-24"
      >
        <option value="">All</option>
        {weeks.map((w) => (
          <option key={w} value={w}>
            {w}
          </option>
        ))}
      </Select>

      <Select
        label="Topic"
        value={values.topic_id}
        onChange={(e) => set({ topic_id: e.target.value })}
        className="w-56"
      >
        <option value="">All topics</option>
        {topics.map((t) => (
          <option key={t.id} value={t.id}>
            {t.name}
          </option>
        ))}
      </Select>

      <Select
        label="Type"
        value={values.type}
        onChange={(e) => set({ type: e.target.value })}
        className="w-24"
      >
        <option value="">All</option>
        {QuestionType.options.map((type) => (
          <option key={type} value={type}>
            {labelFor(TYPE_LABEL, type)}
          </option>
        ))}
      </Select>

      <Select
        label="Difficulty"
        value={values.difficulty}
        onChange={(e) => set({ difficulty: e.target.value })}
        className="w-28"
      >
        <option value="">All</option>
        {Difficulty.options.map((difficulty) => (
          <option key={difficulty} value={difficulty}>
            {labelFor(DIFFICULTY_LABEL, difficulty)}
          </option>
        ))}
      </Select>

      {showStatus ? (
        <>
          <Select
            label="Status"
            value={values.status}
            onChange={(e) => set({ status: e.target.value })}
            className="w-28"
          >
            <option value="">All</option>
            {QuestionStatus.options.map((status) => (
              <option key={status} value={status}>
                {labelFor(STATUS_LABEL, status)}
              </option>
            ))}
          </Select>

          <Select
            label="Source"
            value={values.source}
            onChange={(e) => set({ source: e.target.value })}
            className="w-32"
          >
            <option value="">All</option>
            {QuestionSource.options.map((source) => (
              <option key={source} value={source}>
                {labelFor(SOURCE_LABEL, source)}
              </option>
            ))}
          </Select>
        </>
      ) : null}

      <Input
        label="Search"
        placeholder="Text in the question"
        value={values.q}
        onChange={(e) => set({ q: e.target.value })}
        className="w-56"
      />
    </div>
  );
}
